<script>
import axios from "axios";
import Swal from "sweetalert2";
import { mapGetters } from "vuex";
import {
  getMostRecentAcademicSession,
  sortAcademicSessionsDescending,
} from "@/utils/academicSessionUtils";

const EMPTY_TOTAL = () => ({ count: 0, amount: 0 });

export default {
  name: "PaymentsMgt",
  data() {
    return {
      payments: [],
      paymentTypes: [],
      academicSessions: [],
      programOfferings: [],
      stats: {
        totalRevenue: EMPTY_TOTAL(),
        awaitingVerification: EMPTY_TOTAL(),
        pendingRemittance: EMPTY_TOTAL(),
        todayRevenue: EMPTY_TOTAL(),
      },
      pagination: {
        currentPage: 1,
        itemsPerPage: 10,
        totalItems: 0,
        totalPages: 1,
      },
      filters: {
        status: "",
        paymentType: "",
        program: "",
        remittanceStatus: "",
        dateRange: { start: "", end: "" },
        today: false,
      },
      statsSession: "",
      searchQuery: "",
      searchTimer: null,
      activeStatFilter: "",
      loading: false,
      statsLoading: false,
      refreshing: false,
      exporting: false,
      syncing: false,
      selectedPayment: null,
      settlementResult: null,
      settlementTrackerOpen: false,
      settlementTrackerTab: "outstanding",
      settlementTrackerFilters: { search: "", startDate: "", endDate: "" },
      settlementTrackerTabs: {
        outstanding: {
          rows: [],
          loading: false,
          summary: EMPTY_TOTAL(),
          pagination: { currentPage: 1, totalPages: 1, totalItems: 0 },
        },
        remitted: {
          rows: [],
          loading: false,
          summary: EMPTY_TOTAL(),
          pagination: { currentPage: 1, totalPages: 1, totalItems: 0 },
        },
      },
      settlementTrackerSearchTimer: null,
      settlementTrackerExporting: false,
      initialized: false,
      paymentStatuses: [
        "initiated",
        "pending",
        "successful",
        "failed",
        "reversed",
        "abandoned",
      ],
    };
  },
  computed: {
    ...mapGetters(["hasPermission"]),
    canManagePayments() {
      return this.hasPermission("payments", "manage");
    },
    hasActiveFilters() {
      return Boolean(
        this.searchQuery ||
          this.filters.status ||
          this.filters.paymentType ||
          this.filters.program ||
          this.filters.remittanceStatus ||
          this.filters.dateRange.start ||
          this.filters.dateRange.end ||
          this.filters.today
      );
    },
    pageRange() {
      const total = this.pagination.totalPages;
      const current = this.pagination.currentPage;
      let start = Math.max(1, current - 2);
      const end = Math.min(total, start + 4);
      if (end - start < 4) start = Math.max(1, end - 4);
      return Array.from(
        { length: end - start + 1 },
        (_, index) => start + index
      );
    },
    paginationInfo() {
      const start = this.pagination.totalItems
        ? (this.pagination.currentPage - 1) * this.pagination.itemsPerPage + 1
        : 0;
      return {
        start,
        end: Math.min(
          this.pagination.currentPage * this.pagination.itemsPerPage,
          this.pagination.totalItems
        ),
        total: this.pagination.totalItems,
      };
    },
    availableProgramOfferings() {
      return this.programOfferings
        .filter((offering) => offering.active !== false)
        .map((offering) => ({
          _id: offering._id,
          name: this.programLabelFromOffering(offering),
        }));
    },
    sortedAcademicSessions() {
      return sortAcademicSessionsDescending(this.academicSessions);
    },
    activeSettlementTrackerState() {
      return this.settlementTrackerTabs[this.settlementTrackerTab];
    },
    settlementTrackerPageInfo() {
      const pagination = this.activeSettlementTrackerState.pagination;
      const start = pagination.totalItems
        ? (pagination.currentPage - 1) * 10 + 1
        : 0;
      return {
        start,
        end: Math.min(pagination.currentPage * 10, pagination.totalItems),
        total: pagination.totalItems,
      };
    },
    settlementTrackerLastSyncedAt() {
      const timestamps = Object.values(this.settlementTrackerTabs)
        .map((tab) => tab.summary.lastSyncedAt)
        .filter(Boolean)
        .map((value) => new Date(value).getTime())
        .filter(Number.isFinite);
      return timestamps.length ? new Date(Math.max(...timestamps)) : null;
    },
  },
  watch: {
    searchQuery() {
      if (!this.initialized) return;
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this.fetchPayments(1), 350);
    },
    "pagination.itemsPerPage"() {
      if (this.initialized) this.fetchPayments(1);
    },
    "filters.status"() {
      if (this.initialized) this.fetchPayments(1);
    },
    "filters.paymentType"() {
      if (this.initialized) this.fetchPayments(1);
    },
    "filters.program"() {
      if (this.initialized) this.fetchPayments(1);
    },
    "filters.remittanceStatus"() {
      if (this.initialized) this.fetchPayments(1);
    },
    "filters.dateRange.start"() {
      if (this.initialized) this.fetchPayments(1);
    },
    "filters.dateRange.end"() {
      if (this.initialized) this.fetchPayments(1);
    },
    async statsSession() {
      if (!this.initialized) return;
      const requests = [this.fetchPayments(1), this.fetchStats()];
      if (this.settlementTrackerOpen) {
        requests.push(this.refreshSettlementTracker());
      }
      await Promise.all(requests);
    },
  },
  async mounted() {
    await this.fetchInitialData();
  },
  beforeUnmount() {
    window.clearTimeout(this.searchTimer);
    window.clearTimeout(this.settlementTrackerSearchTimer);
  },
  methods: {
    requestParams(page = 1, limit = this.pagination.itemsPerPage) {
      return {
        page,
        limit,
        search: this.searchQuery.trim() || undefined,
        academicSession: this.statsSession || undefined,
        status: this.filters.status || undefined,
        paymentType: this.filters.paymentType || undefined,
        programOfferingId: this.filters.program || undefined,
        remittanceStatus: this.filters.remittanceStatus || undefined,
        startDate: this.filters.dateRange.start || undefined,
        endDate: this.filters.dateRange.end || undefined,
        today: this.filters.today || undefined,
      };
    },
    async fetchInitialData() {
      this.loading = true;
      try {
        const [sessions, types, offerings] = await Promise.all([
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/academic-sessions`),
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/payments`),
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/program-offerings`),
        ]);
        this.academicSessions = Array.isArray(sessions.data)
          ? sessions.data
          : [];
        this.paymentTypes = Array.isArray(types.data) ? types.data : [];
        this.programOfferings = Array.isArray(offerings.data)
          ? offerings.data
          : [];
        const mostRecentSession = getMostRecentAcademicSession(
          this.academicSessions
        );
        this.statsSession = mostRecentSession?._id || "";
        this.initialized = true;
        await Promise.all([this.fetchPayments(1), this.fetchStats()]);
      } catch (error) {
        this.showError(error, "Failed to load payment management");
      } finally {
        this.loading = false;
      }
    },
    async fetchStats() {
      this.statsLoading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/student-payments/statistics`,
          { params: { academicSession: this.statsSession || undefined } }
        );
        this.stats = { ...this.stats, ...response.data.data };
      } catch (error) {
        this.showError(error, "Failed to load payment statistics");
      } finally {
        this.statsLoading = false;
      }
    },
    async fetchPayments(page = this.pagination.currentPage) {
      this.loading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/student-payments`,
          { params: this.requestParams(page) }
        );
        this.payments = response.data.data.payments || [];
        const pagination = response.data.data.pagination || {};
        this.pagination.currentPage = pagination.page || page;
        this.pagination.totalPages = pagination.pages || 1;
        this.pagination.totalItems = pagination.total || 0;
      } catch (error) {
        this.showError(error, "Failed to fetch payments");
      } finally {
        this.loading = false;
      }
    },
    async refreshPayments() {
      this.refreshing = true;
      await Promise.all([
        this.fetchPayments(this.pagination.currentPage),
        this.fetchStats(),
      ]);
      this.refreshing = false;
    },
    clearFilters() {
      this.initialized = false;
      window.clearTimeout(this.searchTimer);
      this.searchQuery = "";
      this.filters = {
        status: "",
        paymentType: "",
        program: "",
        remittanceStatus: "",
        dateRange: { start: "", end: "" },
        today: false,
      };
      this.activeStatFilter = "";
      this.$nextTick(() => {
        this.initialized = true;
        this.fetchPayments(1);
      });
    },
    applyStatFilter(key) {
      this.initialized = false;
      this.filters.status = "";
      this.filters.remittanceStatus = "";
      this.filters.today = false;
      if (this.activeStatFilter === key) {
        this.activeStatFilter = "";
      } else {
        this.activeStatFilter = key;
        if (key === "totalRevenue") this.filters.status = "successful";
        if (key === "awaitingVerification") this.filters.status = "awaiting";
        if (key === "pendingRemittance") {
          this.filters.status = "successful";
          this.filters.remittanceStatus = "outstanding";
        }
        if (key === "todayRevenue") {
          this.filters.status = "successful";
          this.filters.today = true;
        }
      }
      this.$nextTick(() => {
        this.initialized = true;
        this.fetchPayments(1);
      });
    },
    fullName(payment) {
      const user = payment.payerUserId || {};
      return (
        [user.lastName, user.firstName, user.otherName]
          .filter(Boolean)
          .join(" ") || "N/A"
      );
    },
    identity(payment) {
      return (
        payment.studentId?.matriculationNumber ||
        payment.applicationId?.applicationNumber ||
        "N/A"
      );
    },
    paymentTitle(payment) {
      return (
        payment.payment?.payment?.description ||
        payment.payment?.payment?.name ||
        "N/A"
      );
    },
    programLabelFromOffering(offering) {
      if (!offering) return "N/A";
      return (
        [
          offering.structure?.type?.type,
          offering.structure?.mode?.description ||
            offering.structure?.mode?.mode,
          offering.program?.name,
        ]
          .filter(Boolean)
          .join(" ") || "N/A"
      );
    },
    programLabel(payment) {
      return this.programLabelFromOffering(
        payment.applicationId?.programOfferingId ||
          payment.studentId?.initialProgramOfferingId
      );
    },
    money(value, compact = false) {
      const amount = Number(value || 0);
      if (compact && Math.abs(amount) >= 1000000) {
        return `₦${new Intl.NumberFormat("en", {
          notation: "compact",
          maximumFractionDigits: 1,
        }).format(amount)}`;
      }
      return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        minimumFractionDigits: 2,
      }).format(amount);
    },
    formatDate(value, includeTime = false) {
      if (!value) return "N/A";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "N/A";
      return includeTime
        ? date.toLocaleString("en-GB")
        : date.toLocaleDateString("en-GB");
    },
    statusLabel(value) {
      return String(value || "N/A")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    statusClass(status) {
      return (
        {
          successful: "text-bg-success",
          pending: "text-bg-warning",
          initiated: "text-bg-info",
          failed: "text-bg-danger",
          reversed: "text-bg-secondary",
          abandoned: "text-bg-dark",
        }[status] || "text-bg-secondary"
      );
    },
    viewPaymentDetails(payment) {
      this.selectedPayment = payment;
    },
    closePaymentDetails() {
      this.selectedPayment = null;
    },
    async downloadReceipt(payment) {
      const jsPDF = (await import("jspdf")).default;
      const document = new jsPDF();
      document.setFontSize(18);
      document.text("First City Polytechnic", 15, 20);
      document.setFontSize(14);
      document.text("Payment Receipt", 15, 30);
      document.setFontSize(10);
      const rows = [
        ["Payer", this.fullName(payment)],
        ["Identifier", this.identity(payment)],
        ["Payment", this.paymentTitle(payment)],
        ["Reference", payment.paymentRef],
        ["Amount", this.money(payment.amount)],
        ["Status", this.statusLabel(payment.status)],
        ["Channel", payment.paymentChannel || "N/A"],
        [
          "Date",
          this.formatDate(payment.paymentDate || payment.createdAt, true),
        ],
      ];
      rows.forEach(([label, value], index) => {
        document.setFont(undefined, "bold");
        document.text(`${label}:`, 15, 45 + index * 9);
        document.setFont(undefined, "normal");
        document.text(String(value || "N/A"), 55, 45 + index * 9);
      });
      document.text(
        "This receipt reflects the verified system payment record.",
        15,
        125
      );
      document.save(`receipt_${payment.paymentRef}.pdf`);
    },
    async syncSettlements() {
      this.syncing = true;
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/student-payments/settlement-sync`
        );
        this.settlementResult = response.data.data;
        const refreshes = [this.refreshPayments()];
        if (this.settlementTrackerOpen) {
          refreshes.push(this.refreshSettlementTracker());
        }
        await Promise.all(refreshes);
      } catch (error) {
        this.showError(error, "Settlement synchronisation failed");
      } finally {
        this.syncing = false;
      }
    },
    settlementTrackerParams(tab, page = 1, limit = 10) {
      return {
        page,
        limit,
        remittanceView: tab,
        academicSession: this.statsSession || undefined,
        search: this.settlementTrackerFilters.search.trim() || undefined,
        startDate: this.settlementTrackerFilters.startDate || undefined,
        endDate: this.settlementTrackerFilters.endDate || undefined,
        sortBy: tab === "remitted" ? "settlementDate" : "paymentDate",
        sortOrder: "desc",
      };
    },
    async openSettlementTracker() {
      this.settlementTrackerOpen = true;
      await this.refreshSettlementTracker();
    },
    closeSettlementTracker() {
      this.settlementTrackerOpen = false;
      window.clearTimeout(this.settlementTrackerSearchTimer);
    },
    async refreshSettlementTracker() {
      await Promise.all([
        this.fetchSettlementTrackerTab("outstanding", 1),
        this.fetchSettlementTrackerTab("remitted", 1),
      ]);
    },
    async fetchSettlementTrackerTab(tab, page = 1) {
      const state = this.settlementTrackerTabs[tab];
      if (!state) return;
      state.loading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/student-payments`,
          { params: this.settlementTrackerParams(tab, page) }
        );
        const data = response.data.data || {};
        const pagination = data.pagination || {};
        state.rows = data.payments || [];
        state.summary = {
          count: pagination.total || 0,
          amount: data.totalAmount || 0,
          lastSyncedAt: data.lastSettlementSyncedAt || null,
        };
        state.pagination = {
          currentPage: pagination.page || page,
          totalPages: pagination.pages || 1,
          totalItems: pagination.total || 0,
        };
      } catch (error) {
        this.showError(error, "Failed to load the settlement tracker");
      } finally {
        state.loading = false;
      }
    },
    async changeSettlementTrackerTab(tab) {
      if (!this.settlementTrackerTabs[tab]) return;
      this.settlementTrackerTab = tab;
      if (!this.settlementTrackerTabs[tab].rows.length) {
        await this.fetchSettlementTrackerTab(tab, 1);
      }
    },
    scheduleSettlementTrackerSearch() {
      window.clearTimeout(this.settlementTrackerSearchTimer);
      this.settlementTrackerSearchTimer = window.setTimeout(
        () => this.refreshSettlementTracker(),
        350
      );
    },
    clearSettlementTrackerFilters() {
      window.clearTimeout(this.settlementTrackerSearchTimer);
      this.settlementTrackerFilters = {
        search: "",
        startDate: "",
        endDate: "",
      };
      this.refreshSettlementTracker();
    },
    async fetchAllSettlementTrackerRows(tab) {
      const first = await axios.get(
        `${process.env.VUE_APP_API_BASE_URL}/student-payments`,
        { params: this.settlementTrackerParams(tab, 1, 200) }
      );
      const rows = [...(first.data.data.payments || [])];
      const pages = first.data.data.pagination?.pages || 1;
      for (let page = 2; page <= pages; page += 1) {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/student-payments`,
          { params: this.settlementTrackerParams(tab, page, 200) }
        );
        rows.push(...(response.data.data.payments || []));
      }
      return rows;
    },
    settlementTrackerExportRow(payment) {
      return [
        this.fullName(payment),
        this.identity(payment),
        this.programLabel(payment),
        this.paymentTitle(payment),
        payment.paymentRef,
        payment.amount,
        this.statusLabel(payment.remittanceStatus || "unmatched"),
        this.formatDate(payment.paymentDate || payment.createdAt, true),
        this.formatDate(payment.settlementDate, true),
        payment.settlementId || "",
      ];
    },
    async exportSettlementTracker(format) {
      this.settlementTrackerExporting = true;
      try {
        const rows = await this.fetchAllSettlementTrackerRows(
          this.settlementTrackerTab
        );
        if (!rows.length) {
          await Swal.fire(
            "No Data",
            "There are no records to export",
            "warning"
          );
          return;
        }
        const headers = [
          "Payer",
          "Identifier",
          "Programme",
          "Payment",
          "Reference",
          "Amount",
          "Remittance Status",
          "Paid On",
          "Settlement Date",
          "Settlement ID",
        ];
        const exportRows = rows.map((payment) =>
          this.settlementTrackerExportRow(payment)
        );
        const filename = `settlement-tracker-${
          this.settlementTrackerTab
        }-${new Date().toISOString().slice(0, 10)}`;
        if (format === "csv") {
          const escapeCell = (value) =>
            `"${String(value ?? "").replace(/"/g, '""')}"`;
          const content = [headers, ...exportRows]
            .map((row) => row.map(escapeCell).join(","))
            .join("\n");
          const url = URL.createObjectURL(
            new Blob([content], { type: "text/csv;charset=utf-8" })
          );
          const link = document.createElement("a");
          link.href = url;
          link.download = `${filename}.csv`;
          link.click();
          URL.revokeObjectURL(url);
        } else {
          const jsPDF = (await import("jspdf")).default;
          await import("jspdf-autotable");
          const document = new jsPDF({ orientation: "landscape" });
          document.text("Paystack Settlement Tracker", 14, 16);
          document.autoTable({
            head: [headers],
            body: exportRows,
            startY: 22,
            styles: { fontSize: 7 },
          });
          document.save(`${filename}.pdf`);
        }
      } catch (error) {
        this.showError(error, "Failed to export settlement records");
      } finally {
        this.settlementTrackerExporting = false;
      }
    },
    remittanceClass(status) {
      return (
        {
          remitted: "text-bg-success",
          processing: "text-bg-info",
          pending: "text-bg-warning",
          failed: "text-bg-danger",
          unmatched: "text-bg-secondary",
        }[status] || "text-bg-secondary"
      );
    },
    async fetchAllFilteredPayments() {
      const first = await axios.get(
        `${process.env.VUE_APP_API_BASE_URL}/student-payments`,
        { params: this.requestParams(1, 200) }
      );
      const payments = [...(first.data.data.payments || [])];
      const pages = first.data.data.pagination?.pages || 1;
      for (let page = 2; page <= pages; page += 1) {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/student-payments`,
          { params: this.requestParams(page, 200) }
        );
        payments.push(...(response.data.data.payments || []));
      }
      return payments;
    },
    exportRow(payment) {
      return [
        this.fullName(payment),
        this.identity(payment),
        this.programLabel(payment),
        this.paymentTitle(payment),
        payment.amount,
        payment.paymentRef,
        payment.paymentChannel || "",
        payment.status,
        payment.remittanceStatus,
        this.formatDate(payment.paymentDate || payment.createdAt, true),
      ];
    },
    async exportPayments(format) {
      this.exporting = true;
      try {
        const payments = await this.fetchAllFilteredPayments();
        if (!payments.length) {
          await Swal.fire(
            "No Data",
            "No payments match the current filters",
            "warning"
          );
          return;
        }
        const headers = [
          "Payer",
          "Identifier",
          "Programme",
          "Payment",
          "Amount",
          "Reference",
          "Channel",
          "Status",
          "Remittance",
          "Date",
        ];
        if (format === "csv") {
          const escapeCell = (value) =>
            `"${String(value ?? "").replace(/"/g, '""')}"`;
          const content = [
            headers,
            ...payments.map((payment) => this.exportRow(payment)),
          ]
            .map((row) => row.map(escapeCell).join(","))
            .join("\n");
          const url = URL.createObjectURL(
            new Blob([content], { type: "text/csv" })
          );
          const link = document.createElement("a");
          link.href = url;
          link.download = `payments_${new Date()
            .toISOString()
            .slice(0, 10)}.csv`;
          link.click();
          URL.revokeObjectURL(url);
        } else {
          const jsPDF = (await import("jspdf")).default;
          await import("jspdf-autotable");
          const document = new jsPDF({ orientation: "landscape" });
          document.text("Payment Management Report", 14, 16);
          document.autoTable({
            head: [headers],
            body: payments.map((payment) => this.exportRow(payment)),
            startY: 22,
            styles: { fontSize: 7 },
          });
          document.save(
            `payments_${new Date().toISOString().slice(0, 10)}.pdf`
          );
        }
        await Swal.fire(
          "Success",
          `Exported ${payments.length} payments`,
          "success"
        );
      } catch (error) {
        this.showError(error, "Failed to export payments");
      } finally {
        this.exporting = false;
      }
    },
    showError(error, fallback) {
      Swal.fire("Error", error.response?.data?.message || fallback, "error");
    },
  },
};
</script>

<template>
  <div class="p-4 text-start payments-mgt">
    <div
      class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-end gap-3 mb-4"
    >
      <div>
        <h4 class="fw-bold mb-1">Payment Management</h4>
        <p class="text-muted mb-0">
          Review, filter, export, and manage all payment records.
        </p>
      </div>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <button
          class="btn btn-outline-secondary btn-sm"
          :disabled="loading || refreshing"
          @click="refreshPayments"
        >
          <i
            class="bi bi-arrow-clockwise me-1"
            :class="{ spin: refreshing }"
          ></i
          >Refresh
        </button>
        <button
          class="btn btn-outline-dark btn-sm"
          @click="openSettlementTracker"
        >
          <i class="bi bi-bank me-1"></i>Settlement Tracker
        </button>
        <button
          v-if="canManagePayments"
          class="btn btn-primary btn-sm"
          :disabled="syncing"
          @click="syncSettlements"
        >
          <span
            v-if="syncing"
            class="spinner-border spinner-border-sm me-1"
          ></span
          ><i v-else class="bi bi-bank me-1"></i>Sync settlements
        </button>
        <div class="dropdown">
          <button
            class="btn btn-dark btn-sm dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
            :disabled="exporting"
          >
            <i class="bi bi-download me-1"></i>Export
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li>
              <button class="dropdown-item" @click="exportPayments('csv')">
                <i class="bi bi-filetype-csv me-2"></i>Export CSV
              </button>
            </li>
            <li>
              <button class="dropdown-item" @click="exportPayments('pdf')">
                <i class="bi bi-file-earmark-pdf me-2"></i>Export PDF
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-end mb-3">
      <select
        v-model="statsSession"
        class="form-select form-select-sm stats-session-select"
      >
        <option value="">All sessions</option>
        <option
          v-for="session in sortedAcademicSessions"
          :key="session._id"
          :value="session._id"
        >
          {{ session.sessionYear }}
        </option>
      </select>
    </div>
    <div class="row g-4 mb-5">
      <div
        v-for="card in [
          {
            key: 'totalRevenue',
            label: 'Total Revenue',
            icon: 'bi-piggy-bank',
            tone: 'success',
          },
          {
            key: 'awaitingVerification',
            label: 'Awaiting Verification',
            icon: 'bi-hourglass-split',
            tone: 'warning',
          },
          {
            key: 'pendingRemittance',
            label: 'Pending Remittance',
            icon: 'bi-bank',
            tone: 'primary',
          },
          {
            key: 'todayRevenue',
            label: 'Today’s Revenue',
            icon: 'bi-calendar-check',
            tone: 'info',
          },
        ]"
        :key="card.key"
        class="col-md-3"
      >
        <button
          class="card border-0 shadow-sm py-2 h-100 w-100 text-start stats-filter-card"
          :class="{ 'stats-filter-card-active': activeStatFilter === card.key }"
          @click="applyStatFilter(card.key)"
        >
          <span class="card-body d-flex align-items-center gap-3"
            ><i
              class="bi h4 rounded-circle d-inline-flex align-items-center justify-content-center mb-0"
              :class="[
                card.icon,
                `bg-${card.tone}-subtle`,
                `text-${card.tone}`,
              ]"
              style="width: 3rem; height: 3rem"
            ></i
            ><span
              ><span class="d-block text-body-secondary">{{ card.label }}</span
              ><strong class="fs-4">{{
                statsLoading ? "…" : money(stats[card.key]?.amount, true)
              }}</strong
              ><small class="d-block text-muted"
                >{{ stats[card.key]?.count || 0 }} record(s)</small
              ></span
            ></span
          >
        </button>
      </div>
    </div>

    <div class="card shadow-sm border-0">
      <div class="card-header bg-white">
        <div class="row g-3">
          <div class="col-md-7">
            <div class="input-group">
              <span class="input-group-text bg-light border-end-0"
                ><i class="bi bi-search"></i></span
              ><input
                v-model="searchQuery"
                type="search"
                class="form-control border-start-0"
                placeholder="Search by payer, email, phone, identifier, or reference"
              />
            </div>
          </div>
          <div class="col-md-5 d-flex justify-content-md-end">
            <div class="dropdown">
              <button
                class="btn btn-sm btn-outline-secondary dropdown-toggle"
                data-bs-toggle="dropdown"
              >
                <i class="bi bi-sliders me-1"></i>Filter
              </button>
              <div
                class="dropdown-menu dropdown-menu-end p-3 filter-dropdown"
                @click.stop
              >
                <div class="d-flex justify-content-between mb-3">
                  <strong>Filter Payments</strong
                  ><button
                    v-if="hasActiveFilters"
                    class="btn btn-link btn-sm p-0"
                    @click="clearFilters"
                  >
                    Clear all
                  </button>
                </div>
                <label class="small text-muted">Date Range</label>
                <div class="row g-2 mb-3">
                  <div class="col-6">
                    <input
                      v-model="filters.dateRange.start"
                      type="date"
                      class="form-control form-control-sm"
                    />
                  </div>
                  <div class="col-6">
                    <input
                      v-model="filters.dateRange.end"
                      type="date"
                      class="form-control form-control-sm"
                    />
                  </div>
                </div>
                <label class="small text-muted">Status</label
                ><select
                  v-model="filters.status"
                  class="form-select form-select-sm mb-3"
                >
                  <option value="">All statuses</option>
                  <option
                    v-for="status in paymentStatuses"
                    :key="status"
                    :value="status"
                  >
                    {{ statusLabel(status) }}
                  </option></select
                ><label class="small text-muted">Payment Type</label
                ><select
                  v-model="filters.paymentType"
                  class="form-select form-select-sm mb-3"
                >
                  <option value="">All payment types</option>
                  <option
                    v-for="type in paymentTypes"
                    :key="type._id"
                    :value="type._id"
                  >
                    {{ type.description || type.name }}
                  </option></select
                ><label class="small text-muted">Programme</label
                ><select
                  v-model="filters.program"
                  class="form-select form-select-sm"
                >
                  <option value="">All programmes</option>
                  <option
                    v-for="program in availableProgramOfferings"
                    :key="program._id"
                    :value="program._id"
                  >
                    {{ program.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card-body p-0 table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th class="ps-4">Payer</th>
              <th>Programme</th>
              <th>Payment</th>
              <th>Amount</th>
              <th>Method</th>
              <th>Status</th>
              <th>Date</th>
              <th class="text-end pe-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="8" class="text-center py-5">
                <div class="spinner-border text-primary"></div>
              </td>
            </tr>
            <tr v-else-if="!payments.length">
              <td colspan="8" class="text-center py-5">
                <i class="bi bi-receipt-cutoff fs-2 text-muted d-block"></i
                ><span class="text-muted"
                  >No payments matched the current filters.</span
                >
              </td>
            </tr>
            <tr v-for="payment in payments" v-else :key="payment._id">
              <td class="ps-4 text-capitalize">
                <div>{{ fullName(payment) }}</div>
                <small class="text-muted">{{ identity(payment) }}</small>
              </td>
              <td>{{ programLabel(payment) }}</td>
              <td>
                <div>{{ paymentTitle(payment) }}</div>
                <small class="text-primary bg-primary-subtle rounded px-1">{{
                  payment.paymentRef
                }}</small>
              </td>
              <td>
                <strong>{{ money(payment.amount) }}</strong
                ><small class="d-block text-muted"
                  >Fee: {{ money(payment.paymentFee) }}</small
                >
              </td>
              <td>{{ statusLabel(payment.paymentChannel) }}</td>
              <td>
                <span class="badge" :class="statusClass(payment.status)">{{
                  statusLabel(payment.status)
                }}</span>
              </td>
              <td>
                {{ formatDate(payment.paymentDate || payment.createdAt, true) }}
              </td>
              <td class="text-end pe-4">
                <div class="dropdown">
                  <button
                    class="btn btn-sm btn-light border"
                    data-bs-toggle="dropdown"
                  >
                    <i class="bi bi-three-dots-vertical"></i>
                  </button>
                  <ul class="dropdown-menu dropdown-menu-end">
                    <li>
                      <button
                        class="dropdown-item"
                        @click="viewPaymentDetails(payment)"
                      >
                        <i class="bi bi-eye me-2"></i>View details
                      </button>
                    </li>
                    <li>
                      <button
                        class="dropdown-item"
                        @click="downloadReceipt(payment)"
                      >
                        <i class="bi bi-receipt me-2"></i>Download receipt
                      </button>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="card-footer bg-white py-3">
        <div
          class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3"
        >
          <div class="d-flex align-items-center gap-2">
            <label class="small text-muted">Rows per page</label
            ><select
              v-model.number="pagination.itemsPerPage"
              class="form-select form-select-sm rows-select"
            >
              <option :value="10">10</option>
              <option :value="15">15</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
            </select>
          </div>
          <nav>
            <ul class="pagination pagination-sm mb-0">
              <li
                class="page-item"
                :class="{ disabled: pagination.currentPage === 1 }"
              >
                <button class="page-link" @click="fetchPayments(1)">
                  &laquo;
                </button>
              </li>
              <li
                v-for="page in pageRange"
                :key="page"
                class="page-item"
                :class="{ active: pagination.currentPage === page }"
              >
                <button class="page-link" @click="fetchPayments(page)">
                  {{ page }}
                </button>
              </li>
              <li
                class="page-item"
                :class="{
                  disabled: pagination.currentPage === pagination.totalPages,
                }"
              >
                <button
                  class="page-link"
                  @click="fetchPayments(pagination.totalPages)"
                >
                  &raquo;
                </button>
              </li>
            </ul>
          </nav>
          <small class="text-muted"
            >Showing {{ paginationInfo.start }}-{{ paginationInfo.end }} of
            {{ paginationInfo.total }} payments</small
          >
        </div>
      </div>
    </div>

    <div
      v-if="settlementTrackerOpen"
      class="modal-backdrop-custom"
      @click.self="closeSettlementTracker"
    >
      <div class="modal-card modal-card-tracker bg-white rounded shadow">
        <div
          class="p-3 border-bottom d-flex flex-column flex-md-row justify-content-between gap-3"
        >
          <div>
            <h5 class="mb-1">Settlement Tracker</h5>
            <small class="text-muted">
              Persistent Paystack remittance records
              <span v-if="statsSession">for the selected session</span>
            </small>
            <small class="d-block text-muted">
              Last synchronized:
              {{ formatDate(settlementTrackerLastSyncedAt, true) }}
            </small>
          </div>
          <div class="d-flex align-items-center gap-2">
            <div class="dropdown">
              <button
                class="btn btn-sm btn-outline-dark dropdown-toggle"
                data-bs-toggle="dropdown"
                :disabled="settlementTrackerExporting"
              >
                <i class="bi bi-download me-1"></i>Export
              </button>
              <ul class="dropdown-menu dropdown-menu-end">
                <li>
                  <button
                    class="dropdown-item"
                    @click="exportSettlementTracker('csv')"
                  >
                    Export CSV
                  </button>
                </li>
                <li>
                  <button
                    class="dropdown-item"
                    @click="exportSettlementTracker('pdf')"
                  >
                    Export PDF
                  </button>
                </li>
              </ul>
            </div>
            <button class="btn-close" @click="closeSettlementTracker"></button>
          </div>
        </div>

        <div class="p-4 modal-scroll">
          <div class="row g-3 mb-4">
            <div class="col-md-6">
              <div class="border rounded p-3 bg-warning-subtle h-100">
                <small class="text-muted d-block">Outstanding</small>
                <strong class="fs-5">{{
                  money(settlementTrackerTabs.outstanding.summary.amount)
                }}</strong>
                <span class="small d-block text-muted">
                  {{ settlementTrackerTabs.outstanding.summary.count }}
                  successful payment(s)
                </span>
              </div>
            </div>
            <div class="col-md-6">
              <div class="border rounded p-3 bg-success-subtle h-100">
                <small class="text-muted d-block">Remitted</small>
                <strong class="fs-5">{{
                  money(settlementTrackerTabs.remitted.summary.amount)
                }}</strong>
                <span class="small d-block text-muted">
                  {{ settlementTrackerTabs.remitted.summary.count }} successful
                  payment(s)
                </span>
              </div>
            </div>
          </div>

          <div class="alert alert-light border small">
            This tracker reads saved settlement information. Use
            <strong>Sync Paystack Settlements</strong> to retrieve the latest
            settlement state from Paystack.
          </div>

          <ul class="nav nav-tabs mb-3">
            <li class="nav-item">
              <button
                class="nav-link"
                :class="{ active: settlementTrackerTab === 'outstanding' }"
                @click="changeSettlementTrackerTab('outstanding')"
              >
                Outstanding
                <span class="badge text-bg-secondary ms-1">{{
                  settlementTrackerTabs.outstanding.summary.count
                }}</span>
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link"
                :class="{ active: settlementTrackerTab === 'remitted' }"
                @click="changeSettlementTrackerTab('remitted')"
              >
                Remitted
                <span class="badge text-bg-secondary ms-1">{{
                  settlementTrackerTabs.remitted.summary.count
                }}</span>
              </button>
            </li>
          </ul>

          <div class="border rounded bg-body-tertiary p-3 mb-3">
            <div class="row g-2 align-items-end">
              <div class="col-lg-5">
                <label class="form-label small text-muted mb-1">Search</label>
                <input
                  v-model="settlementTrackerFilters.search"
                  type="search"
                  class="form-control form-control-sm"
                  placeholder="Payer, identifier, reference, or programme"
                  @input="scheduleSettlementTrackerSearch"
                />
              </div>
              <div class="col-lg-3 col-md-5">
                <label class="form-label small text-muted mb-1">From</label>
                <input
                  v-model="settlementTrackerFilters.startDate"
                  type="date"
                  class="form-control form-control-sm"
                  @change="refreshSettlementTracker"
                />
              </div>
              <div class="col-lg-3 col-md-5">
                <label class="form-label small text-muted mb-1">To</label>
                <input
                  v-model="settlementTrackerFilters.endDate"
                  type="date"
                  class="form-control form-control-sm"
                  :min="settlementTrackerFilters.startDate || undefined"
                  @change="refreshSettlementTracker"
                />
              </div>
              <div class="col-lg-1 col-md-2">
                <button
                  class="btn btn-sm btn-outline-secondary w-100"
                  title="Clear tracker filters"
                  @click="clearSettlementTrackerFilters"
                >
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="table-responsive border rounded tracker-table">
            <table class="table table-sm table-hover align-middle mb-0">
              <thead class="table-light sticky-top">
                <tr>
                  <th>Payer</th>
                  <th>Programme</th>
                  <th>Payment</th>
                  <th>Amount</th>
                  <th>Paid On</th>
                  <th>Settlement Date</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="activeSettlementTrackerState.loading">
                  <td colspan="8" class="text-center py-5">
                    <div class="spinner-border spinner-border-sm"></div>
                  </td>
                </tr>
                <tr v-else-if="!activeSettlementTrackerState.rows.length">
                  <td colspan="8" class="text-center text-muted py-5">
                    No settlement records matched the current filters.
                  </td>
                </tr>
                <tr
                  v-for="payment in activeSettlementTrackerState.rows"
                  v-else
                  :key="payment._id"
                >
                  <td>
                    <div class="text-capitalize">{{ fullName(payment) }}</div>
                    <small class="text-muted">{{ identity(payment) }}</small>
                  </td>
                  <td>{{ programLabel(payment) }}</td>
                  <td>
                    <div>{{ paymentTitle(payment) }}</div>
                    <small class="text-primary">{{ payment.paymentRef }}</small>
                  </td>
                  <td>{{ money(payment.amount) }}</td>
                  <td>
                    {{
                      formatDate(payment.paymentDate || payment.createdAt, true)
                    }}
                  </td>
                  <td>{{ formatDate(payment.settlementDate, true) }}</td>
                  <td>
                    <span
                      class="badge"
                      :class="remittanceClass(payment.remittanceStatus)"
                    >
                      {{ statusLabel(payment.remittanceStatus || "unmatched") }}
                    </span>
                  </td>
                  <td class="text-end">
                    <button
                      class="btn btn-sm btn-outline-primary"
                      @click="viewPaymentDetails(payment)"
                    >
                      View
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mt-3"
          >
            <small class="text-muted">
              Showing {{ settlementTrackerPageInfo.start }}-{{
                settlementTrackerPageInfo.end
              }}
              of {{ settlementTrackerPageInfo.total }} records
            </small>
            <div class="d-flex align-items-center gap-2">
              <button
                class="btn btn-sm btn-outline-secondary"
                :disabled="
                  activeSettlementTrackerState.loading ||
                  activeSettlementTrackerState.pagination.currentPage <= 1
                "
                @click="
                  fetchSettlementTrackerTab(
                    settlementTrackerTab,
                    activeSettlementTrackerState.pagination.currentPage - 1
                  )
                "
              >
                Previous
              </button>
              <small>
                Page
                {{ activeSettlementTrackerState.pagination.currentPage }} of
                {{ activeSettlementTrackerState.pagination.totalPages }}
              </small>
              <button
                class="btn btn-sm btn-outline-secondary"
                :disabled="
                  activeSettlementTrackerState.loading ||
                  activeSettlementTrackerState.pagination.currentPage >=
                    activeSettlementTrackerState.pagination.totalPages
                "
                @click="
                  fetchSettlementTrackerTab(
                    settlementTrackerTab,
                    activeSettlementTrackerState.pagination.currentPage + 1
                  )
                "
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="selectedPayment"
      class="modal-backdrop-custom"
      @click.self="closePaymentDetails"
    >
      <div class="modal-card bg-white rounded shadow">
        <div class="p-3 border-bottom d-flex justify-content-between">
          <div>
            <h5 class="mb-0">Payment Details</h5>
            <small class="text-muted">{{ selectedPayment.paymentRef }}</small>
          </div>
          <button class="btn-close" @click="closePaymentDetails"></button>
        </div>
        <div class="p-4">
          <h6 class="fw-bold">Transaction Summary</h6>
          <div class="row g-3 mb-4">
            <div class="col-md-6">
              <strong>Payment:</strong> {{ paymentTitle(selectedPayment) }}
            </div>
            <div class="col-md-6">
              <strong>Amount:</strong> {{ money(selectedPayment.amount) }}
            </div>
            <div class="col-md-6">
              <strong>Status:</strong>
              <span
                class="badge"
                :class="statusClass(selectedPayment.status)"
                >{{ statusLabel(selectedPayment.status) }}</span
              >
            </div>
            <div class="col-md-6">
              <strong>Channel:</strong>
              {{ statusLabel(selectedPayment.paymentChannel) }}
            </div>
            <div class="col-md-6">
              <strong>Date:</strong>
              {{
                formatDate(
                  selectedPayment.paymentDate || selectedPayment.createdAt,
                  true
                )
              }}
            </div>
            <div class="col-md-6">
              <strong>Remittance:</strong>
              {{ statusLabel(selectedPayment.remittanceStatus) }}
            </div>
            <div class="col-md-6">
              <strong>Settlement status:</strong>
              {{ statusLabel(selectedPayment.settlementStatus) }}
            </div>
            <div class="col-md-6">
              <strong>Settlement ID:</strong>
              {{ selectedPayment.settlementId || "N/A" }}
            </div>
            <div class="col-md-6">
              <strong>Settlement date:</strong>
              {{ formatDate(selectedPayment.settlementDate, true) }}
            </div>
            <div class="col-md-6">
              <strong>Last settlement sync:</strong>
              {{ formatDate(selectedPayment.settlementSyncedAt, true) }}
            </div>
          </div>
          <hr />
          <h6 class="fw-bold">Payer Record</h6>
          <div class="row g-3 mb-4">
            <div class="col-md-6">
              <strong>Name:</strong> {{ fullName(selectedPayment) }}
            </div>
            <div class="col-md-6">
              <strong>Identifier:</strong> {{ identity(selectedPayment) }}
            </div>
            <div class="col-md-6">
              <strong>Email:</strong> {{ selectedPayment.payerUserId?.email }}
            </div>
            <div class="col-md-6">
              <strong>Phone:</strong> {{ selectedPayment.payerUserId?.phone }}
            </div>
            <div class="col-12">
              <strong>Programme:</strong> {{ programLabel(selectedPayment) }}
            </div>
          </div>
          <div v-if="selectedPayment.remarks" class="alert alert-light">
            <strong>Remarks:</strong> {{ selectedPayment.remarks }}
          </div>
          <button
            class="btn btn-dark"
            @click="downloadReceipt(selectedPayment)"
          >
            <i class="bi bi-download me-1"></i>Download receipt
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="settlementResult"
      class="modal-backdrop-custom"
      @click.self="settlementResult = null"
    >
      <div class="modal-card modal-card-wide bg-white rounded shadow">
        <div class="p-3 border-bottom d-flex justify-content-between">
          <div>
            <h5 class="mb-0">Settlement Synchronisation Results</h5>
            <small class="text-muted">{{
              formatDate(settlementResult.syncedAt, true)
            }}</small>
          </div>
          <button class="btn-close" @click="settlementResult = null"></button>
        </div>
        <div class="p-4 modal-scroll">
          <div class="row g-3 mb-4">
            <div
              v-for="(value, key) in settlementResult.summary"
              :key="key"
              class="col-md-3"
            >
              <div class="border rounded p-3">
                <small class="text-muted d-block">{{ statusLabel(key) }}</small
                ><strong>{{
                  typeof value === "object" ? "See breakdown" : value
                }}</strong>
              </div>
            </div>
          </div>
          <div class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Payer</th>
                  <th>Identifier</th>
                  <th>Amount</th>
                  <th>Settlement</th>
                  <th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="transaction in settlementResult.transactions"
                  :key="`${transaction.settlementTransactionId}-${transaction.paymentRef}`"
                >
                  <td>{{ transaction.paymentRef || "N/A" }}</td>
                  <td>{{ transaction.studentName }}</td>
                  <td>{{ transaction.studentIdentifier }}</td>
                  <td>{{ money(transaction.amount) }}</td>
                  <td>{{ statusLabel(transaction.remittanceStatus) }}</td>
                  <td>{{ statusLabel(transaction.syncOutcome) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filter-dropdown {
  min-width: 320px;
}
.rows-select {
  width: 88px;
}
.stats-session-select {
  max-width: 240px;
}
.stats-filter-card {
  cursor: pointer;
  transition: 0.15s;
}
.stats-filter-card:hover {
  transform: translateY(-2px);
}
.stats-filter-card-active {
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25) !important;
}
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-card {
  width: min(760px, 100%);
  max-height: 92vh;
  overflow: auto;
}
.modal-card-wide {
  width: min(1100px, 100%);
}
.modal-card-tracker {
  width: min(1240px, 100%);
  max-height: 94vh;
  overflow: hidden;
}
.modal-scroll {
  max-height: 80vh;
  overflow: auto;
}
.tracker-table {
  min-height: 260px;
  max-height: 48vh;
  overflow: auto;
}
.spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
